// publish-completeness.js - "a half pillar never goes out".
//
// Director's rule, 28 Oct 2026: an evaluation may reach a family only if every
// pillar it contains is finished. Three of five pillars done is fine, and the
// family sees three. Three and a half is not, because the half one would read
// as a judgement on a part of her game that nobody actually finished looking at.
//
// WHY THIS LIVES HERE AND NOT IN THE DATABASE. The ceiling rule is enforced by
// a trigger, which cannot be got round. This one cannot be, because the
// database has no idea what a complete pillar is: the rubric lives in
// evaluation-data.js, in code. So it is enforced in db.js, at the two methods
// every publish path goes through, rather than in any one screen. When the
// matrix eventually moves into the database this should move with it.
//
// Pure: no DOM, no network, so the thing that decides what reaches a family is
// testable on its own.

import { getDomains, getSkillsFor } from './evaluation-data.js';

/**
 * How each pillar of one evaluation stands.
 *
 * A pillar is STARTED when at least one of its skills carries a real mark, and
 * COMPLETE when every skill in the rubric does. Levels of 0 or null are "not
 * rated": the editor writes those for a skill the coach skipped, so they must
 * not count towards the total.
 *
 * @param {Array}  ratings  rows of { domain, skill, level }
 * @param {string} format   'gk_v1' for a goalkeeper, else the field rubric
 * @returns {Array} [{ domain, rated, expected, started, complete }]
 */
export function pillarStatus(ratings, format = 'field_v1') {
  const marked = new Map();          // domain -> Set of skills with a real mark
  (ratings || []).forEach(r => {
    if (!r || !r.domain || !r.skill) return;
    if (!(Number(r.level) > 0)) return;
    if (!marked.has(r.domain)) marked.set(r.domain, new Set());
    marked.get(r.domain).add(r.skill);
  });

  return getDomains().map(domain => {
    const expectedSkills = getSkillsFor(domain, format);
    const got = marked.get(domain) || new Set();
    // Count only skills the rubric actually asks for, so a stray row from an
    // older rubric generation cannot make a pillar look finished.
    const rated = expectedSkills.filter(s => got.has(s)).length;
    return {
      domain,
      rated,
      expected: expectedSkills.length,
      started:  rated > 0,
      complete: expectedSkills.length > 0 && rated === expectedSkills.length,
    };
  });
}

/** The pillars that were begun and left unfinished. Empty means safe to send. */
export function incompletePillars(ratings, format = 'field_v1') {
  return pillarStatus(ratings, format).filter(p => p.started && !p.complete);
}

/**
 * Can this evaluation go to the family?
 *
 * Needs at least one finished pillar, because an evaluation with nothing
 * finished has nothing to say, and no unfinished ones.
 */
export function canPublish(ratings, format = 'field_v1') {
  const status = pillarStatus(ratings, format);
  const blocking = status.filter(p => p.started && !p.complete);
  const finished = status.filter(p => p.complete);
  if (blocking.length) {
    return { ok: false, reason: 'incomplete', pillars: blocking };
  }
  if (!finished.length) {
    return { ok: false, reason: 'empty', pillars: [] };
  }
  return { ok: true, reason: null, pillars: finished };
}

/**
 * The refusal, in words a director can act on: which pillar, and how far short.
 * Deliberately names the gap rather than saying "incomplete", so the fix is
 * obvious without opening the evaluation.
 */
export function completenessMessage(result) {
  if (!result || result.ok) return '';
  if (result.reason === 'empty') {
    return 'Nothing to share yet: no pillar has been finished.';
  }
  const parts = result.pillars.map(p => `${p.domain} ${p.rated} of ${p.expected}`);
  return `Not shared. Finish or clear these first: ${parts.join(', ')}.`;
}
